import { useEffect, useRef, useState } from "react";

// The loader doubles as cover while the map tiles + first fleet fetch load in
// the background. Rather than a fixed timer, it holds until the app signals it
// is `ready`, floored/capped so it neither flashes nor hangs on a cold backend.
const PHASE_DURATION_MS = 3800; // dwell on phase 0 before advancing to phase 1
const MIN_TOTAL_MS = 4200;      // never reveal the map before this (narrative floor)
const MAX_TOTAL_MS = 28000;     // hard fallback so we dismiss even if readiness never fires

const PHASES = [
  { label: "Searching for signal",            sub: "Initializing observation session" },
  { label: "Locating signals closest to you", sub: "Resolving nearest asset in pool" },
  { label: "Signal acquired",                 sub: "Secure channel established" },
];

export const LoadingSequence = ({ onComplete, sessionId, ready }) => {
  const [phase, setPhase] = useState(0);
  const [pct, setPct] = useState(6);
  const completedRef = useRef(false);
  // Read `ready` from a ref inside the interval so we don't re-arm timers.
  const readyRef = useRef(ready);
  readyRef.current = ready;

  useEffect(() => {
    const startAt = Date.now();
    const tick = setInterval(() => {
      if (completedRef.current) return;
      const total = Date.now() - startAt;

      // Advance past the opening phase once its dwell elapses.
      if (total >= PHASE_DURATION_MS) setPhase((p) => (p < 1 ? 1 : p));

      // Creep the bar up but ease it toward ~92% so it never reads "done"
      // before the map actually is.
      const crawl = Math.min(92, 6 + (total / MAX_TOTAL_MS) * 100 * 3.2);
      setPct((prev) => Math.max(prev, crawl));

      const canFinish =
        (readyRef.current && total >= MIN_TOTAL_MS) || total >= MAX_TOTAL_MS;
      if (canFinish) {
        completedRef.current = true;
        setPhase(2);
        setPct(100);
        // Brief beat on "Signal acquired" before handing off to the live map.
        setTimeout(onComplete, 850);
      }
    }, 150);
    return () => clearInterval(tick);
  }, [onComplete]);

  const line = PHASES[phase];
  const overallPct = pct;

  return (
    <div className="loader" data-testid="loading-sequence">
      <div className="loader-frame">
        <div className="loader-brand">
          <span className="loader-brand-mark" />
          <span className="loader-brand-name">TRACKER</span>
          <span className="loader-brand-sep">/</span>
          <span className="loader-brand-sub">Observation Client</span>
        </div>

        <div className="loader-stage">
          <div className="loader-label" data-testid={`loading-phase-${phase}`}>
            {line.label}
          </div>
          <div className="loader-sub">{line.sub}</div>

          <div className="loader-bar">
            <div className="loader-bar-fill" style={{ width: `${overallPct}%` }} />
          </div>

          <div className="loader-dots">
            {PHASES.map((_, i) => (
              <span
                key={i}
                className={`loader-dot ${i < phase ? "done" : i === phase ? "active" : ""}`}
              />
            ))}
          </div>
        </div>

        <div className="loader-meta">
          <span>SESSION {sessionId}</span>
          <span className="loader-meta-dot" />
          <span>READ-ONLY</span>
        </div>
      </div>
    </div>
  );
};
